<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { useTicketAdminStore } from '@/stores/useTicketAdminStore'

interface PeriodStat {
  weekStart?: string
  month?: string
  ticketCount: number
  resolvedCount: number
  resolutionRate: number
  resolvedIn7BizDays: number
  resolvedIn30BizDays: number
  avgResolutionBizHours: number | null
  ticketIds: number[]
}

interface StatsPayload {
  totalDevTickets: number
  byWeek: PeriodStat[]
  byMonth: PeriodStat[]
  avgTimeToFirstChangeBizHours: number | null
  avgTimeToFirstTeamActionBizHours: number | null
  avgResolutionBizHours: number | null
  resolvedIn7BizDays: number
  resolvedIn30BizDays: number
  totalResolved: number
  openAtEndOfPeriod: number
  agingBuckets: { lessThan7Days: number; between7And30Days: number; moreThan30Days: number }
  statusDistribution: Record<string, number>
  ticketsWithNoChange: number
  from: string
  to: string
  boardId: number
}

const store = useTicketAdminStore()

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10)
}
function defaultMonday() {
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)
  const monday = new Date(today)
  const dow = today.getUTCDay()
  const diffToMonday = dow === 0 ? -6 : 1 - dow
  monday.setUTCDate(today.getUTCDate() + diffToMonday)
  return isoDate(monday)
}
function defaultToday() {
  return isoDate(new Date())
}

const from = ref(defaultMonday())
const to = ref(defaultToday())
const stats = ref<StatsPayload | null>(null)
const error = ref<string | null>(null)

async function load() {
  error.value = null
  try {
    stats.value = await store.fetchStats({ from: from.value, to: to.value })
  } catch (e) {
    error.value = (e as Error).message
  }
}

async function doExport(format: 'csv' | 'xlsx') {
  try {
    await store.exportStats({ from: from.value, to: to.value, format })
  } catch (e) {
    error.value = (e as Error).message
  }
}

const statusRows = computed(() =>
  stats.value ? Object.entries(stats.value.statusDistribution).sort((a, b) => b[1] - a[1]) : [],
)

onMounted(load)
</script>

<template>
  <div class="p-6 flex flex-col gap-6">
    <div class="flex items-center justify-between flex-wrap gap-3">
      <Bs2Title title="Tickets — stats" subtitle="Indicateurs hebdo/mensuel sur le board DEV" />
      <div class="flex gap-2">
        <button class="btn btn-sm" :disabled="store.loading" @click="doExport('csv')">Export CSV</button>
        <button class="btn btn-sm btn-primary" :disabled="store.loading" @click="doExport('xlsx')">Export XLSX</button>
      </div>
    </div>

    <div class="card bg-base-100 shadow p-4 flex flex-wrap items-end gap-3">
      <label class="form-control">
        <span class="label-text text-xs mb-1">Du</span>
        <input v-model="from" type="date" class="input input-bordered input-sm" />
      </label>
      <label class="form-control">
        <span class="label-text text-xs mb-1">Au</span>
        <input v-model="to" type="date" class="input input-bordered input-sm" />
      </label>
      <button class="btn btn-sm" :disabled="store.loading" @click="load">Charger</button>
      <span v-if="store.loading" class="text-sm text-base-content/60">Chargement…</span>
      <span v-if="error" class="text-sm text-error">Erreur : {{ error }}</span>
    </div>

    <template v-if="stats">
      <!-- KPIs récap -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Tickets DEV</div>
          <div class="text-2xl font-semibold">{{ stats.totalDevTickets }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Résolus</div>
          <div class="text-2xl font-semibold">{{ stats.totalResolved }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Ouverts</div>
          <div class="text-2xl font-semibold">{{ stats.openAtEndOfPeriod }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">MTTR (h ouvrées)</div>
          <div class="text-2xl font-semibold">{{ stats.avgResolutionBizHours ?? '—' }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Réactivité 1er chgt (h ouvrées)</div>
          <div class="text-2xl font-semibold">{{ stats.avgTimeToFirstChangeBizHours ?? '—' }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Réactivité 1ʳᵉ action équipe</div>
          <div class="text-2xl font-semibold">{{ stats.avgTimeToFirstTeamActionBizHours ?? '—' }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Résolus ≤7j ouvrés</div>
          <div class="text-2xl font-semibold">{{ stats.resolvedIn7BizDays }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Résolus ≤30j ouvrés</div>
          <div class="text-2xl font-semibold">{{ stats.resolvedIn30BizDays }}</div>
        </div>
      </div>

      <!-- Hebdo -->
      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-2">Par semaine</h3>
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Semaine</th>
                <th>Nb</th>
                <th>Résolus</th>
                <th>Taux</th>
                <th>≤7j</th>
                <th>≤30j</th>
                <th>MTTR (h)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="w in stats.byWeek" :key="w.weekStart">
                <td>{{ w.weekStart }}</td>
                <td>{{ w.ticketCount }}</td>
                <td>{{ w.resolvedCount }}</td>
                <td>{{ w.resolutionRate }} %</td>
                <td>{{ w.resolvedIn7BizDays }}</td>
                <td>{{ w.resolvedIn30BizDays }}</td>
                <td>{{ w.avgResolutionBizHours ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mensuel -->
      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-2">Par mois</h3>
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Mois</th>
                <th>Nb</th>
                <th>Résolus</th>
                <th>Taux</th>
                <th>≤7j</th>
                <th>≤30j</th>
                <th>MTTR (h)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in stats.byMonth" :key="m.month">
                <td>{{ m.month }}</td>
                <td>{{ m.ticketCount }}</td>
                <td>{{ m.resolvedCount }}</td>
                <td>{{ m.resolutionRate }} %</td>
                <td>{{ m.resolvedIn7BizDays }}</td>
                <td>{{ m.resolvedIn30BizDays }}</td>
                <td>{{ m.avgResolutionBizHours ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="card bg-base-100 shadow p-4">
          <h3 class="font-semibold mb-2">Ancienneté des tickets ouverts</h3>
          <ul class="text-sm">
            <li>&lt; 7 jours : <strong>{{ stats.agingBuckets.lessThan7Days }}</strong></li>
            <li>7–30 jours : <strong>{{ stats.agingBuckets.between7And30Days }}</strong></li>
            <li>&gt; 30 jours : <strong>{{ stats.agingBuckets.moreThan30Days }}</strong></li>
          </ul>
        </div>
        <div class="card bg-base-100 shadow p-4">
          <h3 class="font-semibold mb-2">Répartition par statut</h3>
          <ul class="text-sm">
            <li v-for="[name, count] in statusRows" :key="name">
              {{ name }} : <strong>{{ count }}</strong>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </div>
</template>
